import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import type { TFunction } from 'i18next'

import ActivationTypeChip from '@/features/activation/components/ActivationTypeChip'
import type { Activation } from '@/features/activation/types/activation'
import { ActivationType } from '@/features/activation/types/activation'
import {
  formatDurationWithPlusPrefix,
  formatLocalizedDateTimeMedWithSeconds,
} from '@/features/activation/utils/formatDateTime'
import { getMarketProgramTypes } from '@/features/activation/utils/getMarketProgramTypes'
import { getSiteActivationTimeRange } from '@/features/activation/utils/getSiteActivationTimeRange'
import type { MarketProgram } from '@/features/site/types/marketProgram'
import type { MarketProgramType } from '@/types/marketProgramType'

type ActivationRenderCell = GridRenderCellParams<Activation>

export const SITE_ACTIVATION_COLUMNS = (
  t: TFunction,
  marketPrograms: MarketProgram[] | null,
  language: string,
  timezone: string = 'UTC',
): GridColDef<Activation>[] => [
  {
    field: 'type',
    headerName: t('common.type'),
    flex: 1,
    renderCell: ({ row }: ActivationRenderCell) => (
      <ActivationTypeChip activationType={row.type ?? ActivationType.UNKNOWN} />
    ),
  },
  {
    field: 'startedAt',
    headerName: t('customer_details.activations.resources_activated'),
    flex: 1,
    valueGetter: (_value: Activation['startedAt'], activation: Activation) => {
      const { startedAt } = getSiteActivationTimeRange(activation.activatedResources, activation)
      return formatLocalizedDateTimeMedWithSeconds(timezone ?? '', language, startedAt)
    },
  },
  {
    field: 'endedAt',
    headerName: t('customer_details.activations.resources_deactivated'),
    flex: 1,
    valueGetter: (_value: Activation['endedAt'], activation: Activation) => {
      const { startedAt, endedAt } = getSiteActivationTimeRange(activation.activatedResources, activation)
      return endedAt ? formatDurationWithPlusPrefix(startedAt, endedAt) : '-'
    },
  },
  {
    field: 'marketProgram',
    headerName: t('common.market_program.label'),
    flex: 1,
    type: 'singleSelect',
    valueOptions: Array.from(getMarketProgramTypes(marketPrograms).values()).map((mp) => mp as string),
    getOptionLabel: (value) => (value ? t(`common.market_program.${value as MarketProgramType}`) : '-'),
  },
]
